import React, { useEffect, useState } from 'react'
import { iBusiness } from '../../../pages/business-accounts/diagnostics'
import CustomTab, { Tabs } from '../../molecules/custom-tab/custom-tab'
import { useAuth } from '../../../customHooks/useAuth'
import axios from '../../../utils/axios'
import { BUSINESS_DETAILS } from '../../../constants/routes'
import MetaCards from '../../molecules/meta-card/meta-card'
import { useParams } from 'react-router-dom'
import UsageReporting from '../../molecules/usage-reporting/usage-reporting'
import './business-diagnostics.scss'

interface IBusinessDiagnosticsProps {
  businessData: iBusiness
  isLoading?: boolean
  getMetaInfo: (info: MetaData) => void
  passSelectedTabId: (id: number) => void
  shouldCallMetaDataApi?: number
}

type MetaEntry = {
  nextUrl: string | null
  error: string | null
  status: string
  lastAttemptedAt: number
  syncInterval: number
}

type BusinessUsers = {
  totalBusinessUsers: number
  totalSignedUpUsers: number
}

export type MetaData = Record<string, MetaEntry>

const BusinessDiagnostics: React.FC<IBusinessDiagnosticsProps> = ({
  businessData,
  isLoading,
  getMetaInfo,
  passSelectedTabId,
  shouldCallMetaDataApi = false,
}) => {
  const tabOptions: Tabs[] = [
    { id: 1, title: 'BM Diagnostics' },
    { title: 'BM Usage Reporting', id: 2 },
  ]
  const { user } = useAuth()
  const { id: businessIdParam } = useParams()
  const [activeKey, setActiveKey] = useState(tabOptions[0]?.id)
  const [metaInfo, setMetaInfo] = useState<MetaData>({})
  const businessId = businessData.id || (businessIdParam as string)

  useEffect(() => {
    if (activeKey === 1) {
      getMetaData()
    }
    passSelectedTabId(activeKey)
  }, [activeKey, businessIdParam])

  useEffect(() => {
    getMetaInfo(metaInfo)
  }, [metaInfo])

  useEffect(() => {
    if (shouldCallMetaDataApi) {
      getMetaData()
    }
  }, [shouldCallMetaDataApi])

  const getMetaData = async () => {
    try {
      const config = {
        method: 'GET',
        url: `${BUSINESS_DETAILS}/${businessIdParam}/diagonostics`,
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
            },
      }

      const res = await axios(config)
      let meta = res?.data?.data?.metaInfo
      const businessUsersObj: BusinessUsers = {
        totalBusinessUsers: res?.data?.data?.totalBusinessUsers ?? 0,
        totalSignedUpUsers: res?.data?.data?.totalSignedUpUsers ?? 0,
      }
      meta = {
        ...meta,
        businessUsers: businessUsersObj,
      }
      setMetaInfo(meta || {})
    } catch (err) {
      console.error('Failed to fetch meta data', err)
    }
  }

  const renderDiagnosticsSkeleton = () => (
    <div className="diagnostics-loading-wrapper">
      <h1 className="skeleton-heading" />
      <button className="skeleton-button" />
    </div>
  )

  return (
    <div className="diagnostics">
      {isLoading ? (
        renderDiagnosticsSkeleton()
      ) : (
        <div className="diagnostics-wrapper">
          <h1>
            {businessData?.id} | {businessData?.businessName}
          </h1>
          <CustomTab tabs={tabOptions} activeTab={activeKey} handleTabChange={setActiveKey} />
          {activeKey === 1 ? (
            <MetaCards metaData={metaInfo} />
          ) : (
            <UsageReporting businessId={businessId} />
          )}{' '}
        </div>
      )}
    </div>
  )
}

export default BusinessDiagnostics
